import { useEffect, useState } from 'react'
import textServices from 'services/text-services'
import { dlTrackEvent } from 'components/dataLayer/dataLayer'
import { F1GalleryPhoto, buildPhotoShareUrl, photoDisplayName } from '../gallery'
import { F1VoteResult } from '../hooks/use-pit-stop-gallery'
import StarIcon from './StarIcon'

interface VotePopupProps {
  photo: F1GalleryPhoto
  voted: boolean
  isLogged: boolean
  onVote: (photo: F1GalleryPhoto) => Promise<F1VoteResult>
  onRequireLogin: () => void
  onClose: () => void
}

const VOTE_MESSAGES: Record<Exclude<F1VoteResult, 'ok'>, string> = {
  alreadyVoted: 'Ya has votado esta foto. Puedes votar por otra.',
  maxVotes: 'Llegaste al máximo de votos permitidos.',
  error: 'No pudimos registrar tu voto. Intenta de nuevo.'
}

// Vote popup (Figma 5595:12248): white card over a dim overlay with the
// photo, its code/name, the live vote count and the vote/share actions.
const VotePopup = ({ photo, voted, isLogged, onVote, onRequireLogin, onClose }: VotePopupProps) => {
  const [message, setMessage] = useState<string | null>(null)
  const [shared, setShared] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const handleVote = async () => {
    dlTrackEvent('user_interaction', {
      action: 'click',
      element: `galeria :: votar :: ${photo.code}`,
      section: 'pitstops'
    })
    if (!isLogged) return onRequireLogin()
    if (voted || sending) return
    setSending(true)
    const result = await onVote(photo)
    setSending(false)
    if (result !== 'ok') {
      setMessage(
        textServices.findByText(`pitstops/gallery/vote/${result}`, VOTE_MESSAGES[result]) as string
      )
    }
  }

  const handleShare = async () => {
    dlTrackEvent('user_interaction', {
      action: 'click',
      element: `galeria :: compartir :: ${photo.code}`,
      section: 'pitstops'
    })
    const url = buildPhotoShareUrl(window.location.origin, photo.code)
    const nav = navigator as Navigator & { share?: (data: { url: string }) => Promise<void> }
    if (nav.share) {
      try {
        await nav.share({ url })
        return
      } catch (shareError) {
        // Dismissed share sheet: fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url)
      setShared(true)
      setTimeout(() => setShared(false), 2000)
    } catch (clipboardError) {
      setMessage(url)
    }
  }

  const displayName = photoDisplayName(photo)

  return (
    <div className="f1-vote" role="dialog" aria-label={`Votar por la foto ${photo.code}`}>
      <div className="f1-vote__backdrop" onClick={onClose} />
      <div className="f1-vote__card">
        <button type="button" className="f1-vote__close" aria-label="Cerrar" onClick={onClose}>
          ×
        </button>
        <img className="f1-vote__photo" src={photo.imageUrl} alt={displayName || `Foto ID ${photo.code}`} />
        <div className="f1-vote__meta">
          <div className="f1-vote__identity">
            <span className="f1-vote__code">ID° {photo.code}</span>
            {displayName && <span className="f1-vote__name">{displayName}</span>}
          </div>
          <div className="f1-vote__votes">
            <span className="f1-vote__votes-label">
              {textServices.findByText('pitstops/gallery/votesLabel', 'Votos')}
            </span>
            <span className="f1-vote__votes-count">
              <StarIcon className="f1-vote__votes-star" /> {photo.votes}
            </span>
          </div>
        </div>
        {message && <p className="f1-vote__message">{message}</p>}
        <button
          type="button"
          className="home-v3__button f1-vote__vote"
          disabled={voted || sending}
          onClick={handleVote}
        >
          <StarIcon className="f1-vote__vote-star" />{' '}
          {voted
            ? textServices.findByText('pitstops/gallery/votedCta', 'Votada')
            : textServices.findByText('pitstops/gallery/voteCta', 'Votar')}
        </button>
        <button type="button" className="f1-vote__share" onClick={handleShare}>
          <svg
            className="f1-vote__share-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <path d="m8.6 10.5 6.8-4m-6.8 7 6.8 4" />
          </svg>{' '}
          {shared
            ? textServices.findByText('pitstops/gallery/sharedCta', 'Enlace copiado')
            : textServices.findByText('pitstops/gallery/shareCta', 'Compartir')}
        </button>
      </div>
    </div>
  )
}

export default VotePopup
